import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth, getAuthHeader } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Users, Camera, CheckCircle2, XCircle, Clock, AlertCircle, LogOut, Upload, Download, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeacherView() {
  const { tourId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [tab, setTab] = useState("roster");
  const [today] = useState(new Date().toISOString().slice(0, 10));
  const [uploading, setUploading] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => { load(); }, [tourId]);

  // PWA install prompt wiring
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const dismissed = localStorage.getItem("osaa_install_dismissed") === "1";
    if (standalone || dismissed) return;

    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !/crios|fxios|edgios/.test(ua);
    if (iOS) {
      setIsIOS(true);
      setShowInstallBanner(true);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerInstall = async () => {
    if (isIOS) {
      setShowIOSHelp(true);
      return;
    }
    if (!installPrompt) return;
    installPrompt.prompt();
    try {
      await installPrompt.userChoice;
    } catch (e) { /* noop */ }
    setInstallPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstall = () => {
    localStorage.setItem("osaa_install_dismissed", "1");
    setShowInstallBanner(false);
    setShowIOSHelp(false);
  };

  const load = async () => {
    const h = { headers: getAuthHeader() };
    try {
      const [t, s, a] = await Promise.all([
        axios.get(`${API}/api/portal/tours/${tourId}`, h),
        axios.get(`${API}/api/portal/tours/${tourId}/students`, h),
        axios.get(`${API}/api/portal/tours/${tourId}/attendance?date=${today}`, h),
      ]);
      setTour(t.data);
      setStudents(s.data);
      const map = {};
      (a.data || []).forEach(r => { map[r.student_id] = r.status; });
      setAttendance(map);
    } catch {
      toast.error("Failed to load tour");
    }
  };

  const mark = async (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
    try {
      await axios.post(`${API}/api/portal/tours/${tourId}/attendance`,
        { student_id: studentId, status, date: today },
        { headers: getAuthHeader() });
    } catch {
      toast.error("Save failed");
      load();
    }
  };

  const uploadPhoto = async (e, studentId = "") => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("photo_type", studentId ? "individual" : "common");
        fd.append("student_id", studentId || "");
        fd.append("caption", studentId ? "" : "On-tour upload");
        await axios.post(`${API}/api/admin/tours/${tourId}/gallery`, fd,
          { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const flagged = students.filter(s => s.medical_conditions || s.allergies);
  const present = Object.values(attendance).filter(s => s === "present").length;
  const absent = Object.values(attendance).filter(s => s === "absent").length;
  const late = Object.values(attendance).filter(s => s === "late").length;
  const unmarked = students.length - present - absent - late;

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="teacher-view">
      {/* Sticky header */}
      <header className="sticky top-0 z-20 bg-primary text-primary-foreground px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-primary-foreground/80">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="inline-flex items-center gap-1 text-sm text-primary-foreground/80">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <h1 className="font-heading text-lg font-medium truncate">{tour?.name || "Loading..."}</h1>
        <p className="text-xs text-primary-foreground/70">{tour?.program_type} &middot; {today}</p>
      </header>

      {/* PWA Install Banner */}
      {showInstallBanner && !showIOSHelp && (
        <div className="bg-accent/10 border-b border-accent/30 px-4 py-2.5 flex items-center gap-3" data-testid="install-banner">
          <Download className="w-4 h-4 text-accent shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium leading-tight">Install OSAA Portal</p>
            <p className="text-xs text-muted-foreground leading-tight">Works offline-ready, opens like a native app</p>
          </div>
          <button
            onClick={triggerInstall}
            data-testid="install-install-btn"
            className="px-3 py-1.5 bg-accent text-accent-foreground text-xs font-medium rounded-sm hover:opacity-90"
          >
            Install
          </button>
          <button
            onClick={dismissInstall}
            data-testid="install-dismiss-btn"
            aria-label="Dismiss"
            className="p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* iOS Install Help Sheet */}
      {showIOSHelp && (
        <div className="bg-card border-b border-border/60 px-4 py-3" data-testid="install-ios-help">
          <div className="flex items-start gap-2">
            <Download className="w-4 h-4 text-accent mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-medium mb-1">Install on iPhone / iPad</p>
              <ol className="list-decimal list-inside text-xs text-muted-foreground space-y-0.5">
                <li>Tap the <strong>Share</strong> icon in Safari (square with arrow)</li>
                <li>Scroll and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> - OSAA opens like a native app</li>
              </ol>
            </div>
            <button
              onClick={dismissInstall}
              data-testid="install-ios-dismiss-btn"
              aria-label="Dismiss"
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-4 gap-1 p-3 bg-card border-b border-border/60">
        <Stat label="Total" value={students.length} color="text-foreground" />
        <Stat label="Present" value={present} color="text-green-600" />
        <Stat label="Absent" value={absent} color="text-red-600" />
        <Stat label="Pending" value={unmarked} color="text-amber-600" />
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border/60 bg-background sticky top-[88px] z-10">
        {[
          { id: "roster", label: "Roster" },
          { id: "attendance", label: "Attendance" },
          { id: "photos", label: "Photos" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`teacher-tab-${t.id}`}
            className={`flex-1 px-3 py-2.5 text-sm font-medium border-b-2 ${tab === t.id ? "border-accent text-foreground" : "border-transparent text-muted-foreground"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <main className="p-3 space-y-2">
        {/* Roster */}
        {tab === "roster" && (
          <>
            {flagged.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="text-xs text-red-800">
                  <strong>{flagged.length} student{flagged.length > 1 ? "s" : ""}</strong> with medical alerts - tap below to review.
                </div>
              </div>
            )}
            {students.map(s => (
              <div key={s.id} className="bg-card border border-border/60 rounded-sm p-3 flex items-center gap-3" data-testid={`roster-${s.id}`}>
                {s.photo_url ? (
                  <img src={`${API}${s.photo_url}`} alt="" className="w-12 h-12 rounded-full object-cover border border-border/60" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                    <Users className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{s.full_name}</p>
                    {(s.medical_conditions || s.allergies) && (
                      <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {s.class_name}{s.section ? `-${s.section}` : ""}
                    {s.blood_group ? ` | ${s.blood_group}` : ""}
                    {s.emergency_contact_phone ? ` | ${s.emergency_contact_phone}` : ""}
                  </p>
                  {(s.medical_conditions || s.allergies) && (
                    <p className="text-[11px] text-red-700 mt-0.5 truncate">
                      {[s.medical_conditions, s.allergies].filter(Boolean).join(" | ")}
                    </p>
                  )}
                </div>
              </div>
            ))}
            {students.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">No students on this tour yet.</p>
            )}
          </>
        )}

        {/* Attendance */}
        {tab === "attendance" && (
          <>
            <div className="bg-card border border-border/60 rounded-sm p-3 mb-2 text-xs text-muted-foreground">
              Tap <span className="text-green-600 font-medium">P</span>, <span className="text-amber-600 font-medium">L</span>, or <span className="text-red-600 font-medium">A</span> to mark attendance for {today}.
            </div>
            {students.map(s => (
              <div key={s.id} className="bg-card border border-border/60 rounded-sm p-3 flex items-center gap-2" data-testid={`attendance-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{s.full_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.class_name}{s.section ? `-${s.section}` : ""}</p>
                </div>
                <AttendanceBtn status={attendance[s.id]} target="present" onClick={() => mark(s.id, "present")} label="P" color="green" />
                <AttendanceBtn status={attendance[s.id]} target="late" onClick={() => mark(s.id, "late")} label="L" color="amber" />
                <AttendanceBtn status={attendance[s.id]} target="absent" onClick={() => mark(s.id, "absent")} label="A" color="red" />
              </div>
            ))}
            {students.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-8">No students yet.</p>
            )}
          </>
        )}

        {/* Photos */}
        {tab === "photos" && (
          <div className="space-y-3">
            <div className="bg-card border border-border/60 rounded-sm p-4">
              <p className="text-sm font-medium mb-2">Common / Group Photos</p>
              <p className="text-xs text-muted-foreground mb-3">Upload from your phone's camera or gallery. Snap multiple at once.</p>
              <label className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground text-sm rounded-sm cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`} data-testid="teacher-photo-common">
                <Camera className="w-4 h-4" /> {uploading ? "Uploading..." : "Take / Upload Photos"}
                <input type="file" accept="image/*" capture="environment" multiple onChange={(e) => uploadPhoto(e)} className="hidden" />
              </label>
            </div>
            <div className="bg-card border border-border/60 rounded-sm p-4">
              <p className="text-sm font-medium mb-2">Photo for a specific student</p>
              <p className="text-xs text-muted-foreground mb-2">Select student, then take a photo:</p>
              {students.map(s => (
                <label key={s.id} className="flex items-center gap-2 py-2 border-b border-border/40 last:border-0" data-testid={`teacher-photo-student-${s.id}`}>
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                    {s.photo_url ? <img src={`${API}${s.photo_url}`} alt="" className="w-full h-full rounded-full object-cover" /> : <Users className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <span className="flex-1 text-sm truncate">{s.full_name}</span>
                  <span className="inline-flex items-center gap-1 text-xs text-primary">
                    <Upload className="w-3.5 h-3.5" /> Upload
                  </span>
                  <input type="file" accept="image/*" capture="environment" onChange={(e) => uploadPhoto(e, s.id)} className="hidden" disabled={uploading} />
                </label>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className={`font-heading text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function AttendanceBtn({ status, target, onClick, label, color }) {
  const active = status === target;
  const bg = {
    green: active ? "bg-green-600 text-white border-green-600" : "border-green-600/40 text-green-700 hover:bg-green-50",
    amber: active ? "bg-amber-500 text-white border-amber-500" : "border-amber-500/40 text-amber-700 hover:bg-amber-50",
    red:   active ? "bg-red-600 text-white border-red-600" : "border-red-600/40 text-red-700 hover:bg-red-50",
  }[color];
  return (
    <button
      onClick={onClick}
      data-testid={`attendance-btn-${target}`}
      className={`w-9 h-9 rounded-full border font-semibold text-sm transition-colors ${bg}`}
    >
      {label}
    </button>
  );
}
